import React, { useState } from "react";
import { ChatSummary } from "../../../api/admin/types";

interface Props {
  chats: ChatSummary[];
  loading: boolean;
  selectedChatId: string;
  onSelect: (chatId: string) => void;
  onNew: () => void;
  onDelete: (chatId: string) => void;
  // True while a message is streaming. "New chat" and switching chats both
  // race with the in-flight send() completion handler (see useAdminAgent's
  // `send`), so we disable them while a reply is in progress rather than let
  // the streamed reply potentially land in the wrong chat.
  isThinking?: boolean;
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
  return d.toLocaleDateString();
}

const ChatSidebar: React.FC<Props> = ({
  chats,
  loading,
  selectedChatId,
  onSelect,
  onNew,
  onDelete,
  isThinking = false,
}) => {
  const [hovering, setHovering] = useState<string | null>(null);

  return (
    <div
      style={{
        width: 220,
        borderRight: "1px solid #f1f5f9",
        background: "#fcfcfd",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div style={{ padding: 12, borderBottom: "1px solid #f1f5f9" }}>
        <button
          onClick={onNew}
          disabled={isThinking}
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#3949ab",
            fontSize: 13,
            fontWeight: 600,
            cursor: isThinking ? "not-allowed" : "pointer",
            opacity: isThinking ? 0.5 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <span aria-hidden="true">+</span> New chat
        </button>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {loading && chats.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 12, padding: 12 }}>Loading…</div>
        ) : chats.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 12, padding: 12 }}>
            No chats yet. Send a message to start one.
          </div>
        ) : (
          chats.map((c) => {
            const active = c.chat_id === selectedChatId;
            const showDelete = hovering === c.chat_id || active;
            const locked = isThinking && !active;
            return (
              <div
                key={c.chat_id}
                onMouseEnter={() => setHovering(c.chat_id)}
                onMouseLeave={() => setHovering((h) => (h === c.chat_id ? null : h))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: active ? "#eef2ff" : "transparent",
                  cursor: locked ? "not-allowed" : "pointer",
                  opacity: locked ? 0.5 : 1,
                }}
                onClick={() => {
                  if (isThinking) return;
                  onSelect(c.chat_id);
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      color: active ? "#3730a3" : "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.title || "Untitled"}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                    {relativeTime(c.updated_at)}
                  </div>
                </div>
                {showDelete ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isThinking) return;
                      if (window.confirm("Delete this chat?")) onDelete(c.chat_id);
                    }}
                    disabled={isThinking}
                    aria-label="Delete chat"
                    title="Delete chat"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#9ca3af",
                      cursor: isThinking ? "not-allowed" : "pointer",
                      opacity: isThinking ? 0.5 : 1,
                      padding: 4,
                      borderRadius: 4,
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                    onMouseEnter={(e) => {
                      if (isThinking) return;
                      e.currentTarget.style.color = "#dc2626";
                      e.currentTarget.style.background = "#fef2f2";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#9ca3af";
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
